/**
 * DocumentUpload Page
 *
 * Full upload interface for MHR documents with patient selection.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Upload,
  User,
  Search,
  Plus,
  FileText,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  ChevronDown,
  X,
  Loader2,
  ClipboardList,
  FlaskConical,
  RefreshCw,
} from 'lucide-react';
import { useMHR } from '../contexts';
import {
  DropZone,
  BatchUploadList,
  AsyncProcessingStatus,
  CostEstimateModal,
} from '../components/Upload';
import { Breadcrumb } from '../components/Breadcrumb';
import { Toast } from '../components/Toast';
import { PageHeader } from '../../ui';
import * as mhrApi from '../lib/api';
import type { MHRPatient, MHRDocument, UploadFile, CostEstimate } from '../types';

// Generate unique ID for upload files
function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}

export function DocumentUpload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { protocols, currentProtocol, setCurrentProtocol } = useMHR();

  // Refs for click-outside handling
  const dropdownRef = useRef<HTMLDivElement>(null);
  const protocolDropdownRef = useRef<HTMLDivElement>(null);

  // Patient selection state
  const [patients, setPatients] = useState<MHRPatient[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<MHRPatient | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  // Protocol selection state
  const [protocolSearch, setProtocolSearch] = useState('');
  const [showProtocolDropdown, setShowProtocolDropdown] = useState(false);

  // New patient form state
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [newPatientFirstName, setNewPatientFirstName] = useState('');
  const [newPatientLastName, setNewPatientLastName] = useState('');
  const [newPatientDob, setNewPatientDob] = useState('');
  const [creatingPatient, setCreatingPatient] = useState(false);

  // Upload state
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<MHRDocument[]>([]);

  // Processing state
  const [processingDocId, setProcessingDocId] = useState<string | null>(null);
  const [costEstimate, setCostEstimate] = useState<CostEstimate | null>(null);
  const [showCostModal, setShowCostModal] = useState(false);
  const [confirmingCost, setConfirmingCost] = useState(false);

  // Async processing state - track documents with active async jobs
  const [asyncProcessingDocs, setAsyncProcessingDocs] = useState<Set<string>>(new Set());

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Success notification state
  const [success, setSuccess] = useState<string | null>(null);
  const clearSuccess = useCallback(() => setSuccess(null), []);

  // Reprocessing state
  const [reprocessingDocId, setReprocessingDocId] = useState<string | null>(null);

  // Track if user has acknowledged warnings for proceeding
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(false);

  // Initialize from search params
  useEffect(() => {
    const protocolId = searchParams.get('protocol_id');
    if (protocolId) {
      const protocol = protocols.find(p => p.id === protocolId);
      if (protocol && (!currentProtocol || currentProtocol.id !== protocolId)) {
        setCurrentProtocol(protocol);
      }
    }
  }, [searchParams, protocols, currentProtocol, setCurrentProtocol]);

  // Load patients
  const loadPatients = useCallback(async (search?: string) => {
    setPatientsLoading(true);
    try {
      const result = await mhrApi.listPatients({
        search,
        limit: 20,
      });
      setPatients(result.data);
    } catch (err: any) {
      console.error('[DocumentUpload] Error loading patients:', err);
      setError(err.response?.data?.error || 'Failed to load patients');
    } finally {
      setPatientsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // Click outside handler to close dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowPatientDropdown(false);
      }
      if (protocolDropdownRef.current && !protocolDropdownRef.current.contains(event.target as Node)) {
        setShowProtocolDropdown(false);
      }
    }

    if (showPatientDropdown || showProtocolDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPatientDropdown, showProtocolDropdown]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (patientSearch) {
        loadPatients(patientSearch);
      } else {
        loadPatients();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch, loadPatients]);

  // Filter patients for dropdown (client-side filter for already loaded patients)
  const filteredPatients = useMemo(() => {
    if (!patientSearch) return patients;
    const search = patientSearch.toLowerCase();
    return patients.filter(
      p =>
        p.internal_id.toLowerCase().includes(search) ||
        (p.mrn && p.mrn.toLowerCase().includes(search)) ||
        (p.name && p.name.toLowerCase().includes(search)) ||
        (p.first_name && p.first_name.toLowerCase().includes(search)) ||
        (p.last_name && p.last_name.toLowerCase().includes(search))
    );
  }, [patients, patientSearch]);

  // Filter protocols for dropdown
  const filteredProtocols = useMemo(() => {
    // Only show active protocols
    const activeProtocols = protocols.filter(p => p.status === 'active');
    if (!protocolSearch) return activeProtocols;
    const search = protocolSearch.toLowerCase();
    return activeProtocols.filter(
      p =>
        p.protocol_number.toLowerCase().includes(search) ||
        (p.protocol_title && p.protocol_title.toLowerCase().includes(search)) ||
        (p.sponsor_name && p.sponsor_name.toLowerCase().includes(search))
    );
  }, [protocols, protocolSearch]);

  // Handle patient selection
  const handleSelectPatient = (patient: MHRPatient) => {
    setSelectedPatient(patient);
    setShowPatientDropdown(false);
    setPatientSearch('');
    // Load documents for this patient
    loadPatientDocuments(patient.id);
  };

  // Handle protocol selection
  const handleSelectProtocol = (protocol: typeof protocols[0]) => {
    setCurrentProtocol(protocol);
    setShowProtocolDropdown(false);
    setProtocolSearch('');
  };

  // Load documents for selected patient
  const loadPatientDocuments = async (patientId: string) => {
    try {
      const docs = await mhrApi.listDocuments(patientId);
      setUploadedDocuments(docs);
    } catch (err) {
      console.error('[DocumentUpload] Error loading documents:', err);
    }
  };

  // Handle create new patient
  const handleCreatePatient = async () => {
    if (!newPatientFirstName.trim() || !newPatientLastName.trim() || !newPatientDob) {
      setError('First name, last name, and date of birth are required');
      return;
    }

    setCreatingPatient(true);
    setError(null);

    try {
      const patient = await mhrApi.createPatient({
        firstName: newPatientFirstName.trim(),
        lastName: newPatientLastName.trim(),
        dob: newPatientDob,
      });

      setPatients(prev => [patient, ...prev]);
      setSelectedPatient(patient);
      setShowNewPatientForm(false);
      setNewPatientFirstName('');
      setNewPatientLastName('');
      setNewPatientDob('');
      setSuccess('Patient created successfully');
    } catch (err: any) {
      console.error('[DocumentUpload] Error creating patient:', err);
      setError(err.response?.data?.error || 'Failed to create patient');
    } finally {
      setCreatingPatient(false);
    }
  };

  // Handle files selected from drop zone
  const handleFilesSelected = (files: File[]) => {
    const newUploadFiles: UploadFile[] = files.map(file => ({
      file,
      id: generateId(),
      status: 'pending',
      progress: 0,
    }));
    setUploadFiles(prev => [...prev, ...newUploadFiles]);
  };

  // Handle remove file from list
  const handleRemoveFile = (fileId: string) => {
    setUploadFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Handle upload all files
  const handleUploadAll = async () => {
    if (!selectedPatient || uploadFiles.length === 0) return;

    setUploading(true);
    setError(null);

    const pendingFiles = uploadFiles.filter(f => f.status === 'pending');

    for (const uploadFile of pendingFiles) {
      // Update status to uploading
      setUploadFiles(prev =>
        prev.map(f => (f.id === uploadFile.id ? { ...f, status: 'uploading' as const } : f))
      );

      try {
        const document = await mhrApi.uploadDocument(
          selectedPatient.id,
          uploadFile.file,
          progress => {
            setUploadFiles(prev =>
              prev.map(f => (f.id === uploadFile.id ? { ...f, progress } : f))
            );
          }
        );

        // Update status to success
        setUploadFiles(prev =>
          prev.map(f =>
            f.id === uploadFile.id
              ? { ...f, status: 'success' as const, progress: 100, document }
              : f
          )
        );

        // Add to uploaded documents
        setUploadedDocuments(prev => [...prev, document]);
      } catch (err: any) {
        console.error('[DocumentUpload] Upload error:', err);
        setUploadFiles(prev =>
          prev.map(f =>
            f.id === uploadFile.id
              ? {
                  ...f,
                  status: 'error' as const,
                  error: err.response?.data?.error || 'Upload failed',
                  // Keep file reference for retry
                  canRetry: true,
                }
              : f
          )
        );
      }
    }

    setUploading(false);
  };

  // Handle retry failed upload
  const handleRetryUpload = async (fileId: string) => {
    const uploadFile = uploadFiles.find(f => f.id === fileId);
    if (!uploadFile || !selectedPatient) return;

    // Reset status to uploading
    setUploadFiles(prev =>
      prev.map(f => (f.id === fileId ? { ...f, status: 'uploading' as const, error: undefined, progress: 0 } : f))
    );

    try {
      const document = await mhrApi.uploadDocument(
        selectedPatient.id,
        uploadFile.file,
        progress => {
          setUploadFiles(prev =>
            prev.map(f => (f.id === fileId ? { ...f, progress } : f))
          );
        }
      );

      // Update status to success
      setUploadFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'success' as const, progress: 100, document, canRetry: false }
            : f
        )
      );

      // Add to uploaded documents
      setUploadedDocuments(prev => [...prev, document]);
    } catch (err: any) {
      console.error('[DocumentUpload] Retry upload error:', err);
      setUploadFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? {
                ...f,
                status: 'error' as const,
                error: err.response?.data?.error || 'Upload failed',
                canRetry: true,
              }
            : f
        )
      );
    }
  };

  // Handle get cost estimate
  const handleGetCostEstimate = async (documentId: string) => {
    setProcessingDocId(documentId);
    try {
      const estimate = await mhrApi.getCostEstimate(documentId, currentProtocol?.id);
      setCostEstimate(estimate);
      setShowCostModal(true);
    } catch (err: any) {
      console.error('[DocumentUpload] Error getting cost estimate:', err);
      setError(err.response?.data?.error || 'Failed to get cost estimate');
    }
  };

  // Handle confirm processing - uses async processing with fallback to sync
  const handleConfirmProcessing = async () => {
    if (!processingDocId) return;

    setConfirmingCost(true);
    try {
      // Use async processing (will fallback to sync if queue unavailable)
      await mhrApi.startAsyncProcessing(processingDocId);

      // Track this document as being processed
      setAsyncProcessingDocs(prev => new Set(prev).add(processingDocId));

      setShowCostModal(false);
      setCostEstimate(null);
      setSuccess('Document processing started');

      // Refresh document list to show updated status
      if (selectedPatient) {
        loadPatientDocuments(selectedPatient.id);
      }
    } catch (err: any) {
      console.error('[DocumentUpload] Error starting async processing:', err);
      setError(err.response?.data?.error || 'Failed to start processing');
    } finally {
      setConfirmingCost(false);
    }
  };

  // Handle async processing completion - refresh document list and remove from tracking
  const handleAsyncProcessingComplete = useCallback((documentId: string) => {
    setAsyncProcessingDocs(prev => {
      const next = new Set(prev);
      next.delete(documentId);
      return next;
    });

    // Refresh document list to get updated status
    if (selectedPatient) {
      loadPatientDocuments(selectedPatient.id);
    }
  }, [selectedPatient]);

  // Handle async processing cancelled
  const handleAsyncProcessingCancel = useCallback((documentId: string) => {
    setAsyncProcessingDocs(prev => {
      const next = new Set(prev);
      next.delete(documentId);
      return next;
    });

    // Refresh document list
    if (selectedPatient) {
      loadPatientDocuments(selectedPatient.id);
    }
  }, [selectedPatient]);

  // Check if documents are ready for review (completed or completed with errors)
  const documentsReady = useMemo(() => {
    return (
      selectedPatient &&
      uploadedDocuments.some(
        d => d.extraction_status === 'completed' || d.extraction_status === 'completed_with_errors'
      )
    );
  }, [selectedPatient, uploadedDocuments]);

  // Analyze document statuses for warnings
  const documentStatusAnalysis = useMemo(() => {
    const hasCompletedWithErrors = uploadedDocuments.some(
      d => d.extraction_status === 'completed_with_errors'
    );
    const hasFailedDocuments = uploadedDocuments.some(d => d.extraction_status === 'failed');
    const hasPendingDocuments = uploadedDocuments.some(d => d.extraction_status === 'pending');
    const hasProcessingDocuments = uploadedDocuments.some(
      d =>
        d.extraction_status === 'extracting' ||
        d.extraction_status === 'ocr_in_progress' ||
        d.extraction_status === 'vision_verification'
    );
    const allCompleted = uploadedDocuments.every(d => d.extraction_status === 'completed');

    return {
      hasCompletedWithErrors,
      hasFailedDocuments,
      hasPendingDocuments,
      hasProcessingDocuments,
      allCompleted,
      showWarning: hasCompletedWithErrors || hasFailedDocuments,
    };
  }, [uploadedDocuments]);

  // Check if can proceed to review (documents ready + protocol selected)
  const canProceed = useMemo(() => {
    // Can proceed if documents are ready and protocol is selected
    // For completed_with_errors, user must acknowledge warnings first
    if (!documentsReady || !currentProtocol) return false;

    // If all documents are fully completed, can proceed
    if (documentStatusAnalysis.allCompleted) return true;

    // If there are warnings but user acknowledged, can proceed
    if (documentStatusAnalysis.showWarning && acknowledgedWarnings) return true;

    // If only completed_with_errors (no failures), can proceed with warning shown
    if (
      documentStatusAnalysis.hasCompletedWithErrors &&
      !documentStatusAnalysis.hasFailedDocuments
    ) {
      return true;
    }

    return false;
  }, [documentsReady, currentProtocol, documentStatusAnalysis, acknowledgedWarnings]);

  // Handle reprocessing a document
  const handleReprocessDocument = async (documentId: string) => {
    setReprocessingDocId(documentId);
    setError(null);

    try {
      await mhrApi.startAsyncProcessing(documentId);

      // Track this document as being processed
      setAsyncProcessingDocs(prev => new Set(prev).add(documentId));

      // Refresh document list to show updated status
      if (selectedPatient) {
        await loadPatientDocuments(selectedPatient.id);
      }
    } catch (err: any) {
      console.error('[DocumentUpload] Error reprocessing document:', err);
      setError(err.response?.data?.error || 'Failed to reprocess document');
    } finally {
      setReprocessingDocId(null);
    }
  };

  // Handle navigate to review
  const handleProceedToReview = () => {
    if (selectedPatient && currentProtocol) {
      navigate(`/mhr/reviews/new?patient_id=${selectedPatient.id}&protocol_id=${currentProtocol.id}`);
    } else if (selectedPatient) {
      navigate(`/mhr/reviews/new?patient_id=${selectedPatient.id}`);
    }
  };

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/mhr' },
    ...(currentProtocol
      ? [{ label: currentProtocol.protocol_number, href: `/mhr/protocols/${currentProtocol.id}` }]
      : []),
    { label: 'Upload' },
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <PageHeader
        title="Upload Medical History"
        icon={Upload}
        subtitle="Upload patient medical history documents for eligibility review"
      />

      {/* Error alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            aria-label="Dismiss error"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Protocol selector (optional) */}
      {currentProtocol && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <div>
              <p className="text-sm font-medium text-primary-900 dark:text-primary-300">
                Protocol: {currentProtocol.protocol_number}
              </p>
              {currentProtocol.protocol_title && (
                <p className="text-xs text-primary-700 dark:text-primary-400">{currentProtocol.protocol_title}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Patient Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
            <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Select Patient</h2>
        </div>

        {selectedPatient ? (
          <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4">
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {selectedPatient.first_name && selectedPatient.last_name
                  ? `${selectedPatient.first_name} ${selectedPatient.last_name}`
                  : selectedPatient.name || selectedPatient.internal_id}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {selectedPatient.dob && <span>DOB: {new Date(selectedPatient.dob + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · </span>}
                {selectedPatient.mrn && <span>MRN: {selectedPatient.mrn} · </span>}
                <span>{selectedPatient.internal_id}</span>
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedPatient(null);
                setUploadedDocuments([]);
              }}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Patient search dropdown */}
            <div className="relative" ref={dropdownRef}>
              <div
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer flex items-center justify-between hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                onClick={() => setShowPatientDropdown(!showPatientDropdown)}
              >
                <span className="text-gray-500 dark:text-gray-400">Select a patient...</span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${showPatientDropdown ? 'rotate-180' : ''}`}
                />
              </div>

              {/* Dropdown */}
              {showPatientDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-80 overflow-hidden">
                  {/* Search input inside dropdown */}
                  <div className="p-2 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        placeholder="Search by name, MRN, or ID..."
                        value={patientSearch}
                        onChange={e => setPatientSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Patient list */}
                  <div className="max-h-60 overflow-y-auto">
                    {patientsLoading ? (
                      <div className="p-4 text-center">
                        <Loader2 className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-spin mx-auto" />
                      </div>
                    ) : filteredPatients.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        No patients found
                      </div>
                    ) : (
                      filteredPatients.map(patient => (
                        <button
                          key={patient.id}
                          onClick={() => handleSelectPatient(patient)}
                          className="w-full px-4 py-3 text-left hover:bg-primary-50 dark:hover:bg-primary-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors min-h-[44px] sm:min-h-0"
                        >
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {patient.first_name && patient.last_name
                              ? `${patient.first_name} ${patient.last_name}`
                              : patient.name || patient.internal_id}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {patient.dob && <span>DOB: {new Date(patient.dob + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                            {patient.dob && patient.mrn && <span> · </span>}
                            {patient.mrn && <span>MRN: {patient.mrn}</span>}
                            {!patient.dob && !patient.mrn && (
                              <span>Added {new Date(patient.created_at).toLocaleDateString()}</span>
                            )}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Create new patient button/form */}
            {showNewPatientForm ? (
              <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Create New Patient</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name <span className="text-red-500 dark:text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={newPatientFirstName}
                      onChange={e => setNewPatientFirstName(e.target.value)}
                      placeholder="First name"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name <span className="text-red-500 dark:text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={newPatientLastName}
                      onChange={e => setNewPatientLastName(e.target.value)}
                      placeholder="Last name"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date of Birth <span className="text-red-500 dark:text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    value={newPatientDob}
                    onChange={e => setNewPatientDob(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    A unique MRN will be auto-generated. All PHI is encrypted at rest.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCreatePatient}
                    disabled={creatingPatient || !newPatientFirstName.trim() || !newPatientLastName.trim() || !newPatientDob}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
                  >
                    {creatingPatient ? 'Creating...' : 'Create Patient'}
                  </button>
                  <button
                    onClick={() => {
                      setShowNewPatientForm(false);
                      setNewPatientFirstName('');
                      setNewPatientLastName('');
                      setNewPatientDob('');
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px] sm:min-h-0"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowNewPatientForm(true)}
                className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
              >
                <Plus size={16} />
                Create New Patient
              </button>
            )}
          </div>
        )}
      </div>

      {/* Upload Section */}
      {selectedPatient && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
              <Upload className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Upload Documents</h2>
          </div>

          {/* Drop zone */}
          <DropZone onFilesSelected={handleFilesSelected} disabled={uploading} />

          {/* Selected files list */}
          {uploadFiles.length > 0 && (
            <div className="mt-4 space-y-4">
              <BatchUploadList files={uploadFiles} onRemove={handleRemoveFile} onRetry={handleRetryUpload} />

              {/* Upload button */}
              {uploadFiles.some(f => f.status === 'pending') && (
                <div className="flex justify-end">
                  <button
                    onClick={handleUploadAll}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
                  >
                    {uploading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={18} />
                        Upload All Files
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Uploaded Documents */}
      {selectedPatient && uploadedDocuments.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
              <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Uploaded Documents</h2>
          </div>

          <div className="space-y-3">
            {uploadedDocuments.map(doc => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{doc.filename}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {doc.page_count > 0 ? `${doc.page_count} pages` : 'Processing...'} -{' '}
                      {(doc.file_size / 1024 / 1024).toFixed(1)} MB
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {doc.extraction_status === 'pending' && !asyncProcessingDocs.has(doc.id) && (
                    <button
                      onClick={() => handleGetCostEstimate(doc.id)}
                      className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors min-h-[44px] sm:min-h-0"
                    >
                      Start Processing
                    </button>
                  )}
                  {/* Show async processing status for documents being processed */}
                  {(doc.extraction_status === 'extracting' ||
                    doc.extraction_status === 'ocr_in_progress' ||
                    doc.extraction_status === 'vision_verification' ||
                    asyncProcessingDocs.has(doc.id)) && (
                    <AsyncProcessingStatus
                      documentId={doc.id}
                      autoStart={false}
                      onComplete={() => handleAsyncProcessingComplete(doc.id)}
                      onCancel={() => handleAsyncProcessingCancel(doc.id)}
                    />
                  )}
                  {doc.extraction_status === 'completed' && (
                    <>
                      <span className="px-3 py-1.5 text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        Ready for Review
                      </span>
                      <button
                        onClick={() => navigate(`/mhr/documents/${doc.id}/summary`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors min-h-[44px] sm:min-h-0"
                      >
                        <ClipboardList size={14} />
                        View Summary
                      </button>
                    </>
                  )}
                  {doc.extraction_status === 'completed_with_errors' && (
                    <>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <AlertTriangle size={14} />
                        Partial Extraction
                      </span>
                      <button
                        onClick={() => handleReprocessDocument(doc.id)}
                        disabled={reprocessingDocId === doc.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-yellow-700 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700 rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
                      >
                        {reprocessingDocId === doc.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <RefreshCw size={14} />
                        )}
                        Reprocess
                      </button>
                      <button
                        onClick={() => navigate(`/mhr/documents/${doc.id}/summary`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors min-h-[44px] sm:min-h-0"
                      >
                        <ClipboardList size={14} />
                        View Summary
                      </button>
                    </>
                  )}
                  {doc.extraction_status === 'failed' && (
                    <>
                      <span className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        Failed
                      </span>
                      <button
                        onClick={() => handleReprocessDocument(doc.id)}
                        disabled={reprocessingDocId === doc.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
                      >
                        {reprocessingDocId === doc.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <RefreshCw size={14} />
                        )}
                        Try Again
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Protocol Selection - shows when documents are ready */}
      {documentsReady && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
              <FlaskConical className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Select Protocol for Review</h2>
          </div>

          {currentProtocol ? (
            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{currentProtocol.protocol_number}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {currentProtocol.protocol_title && <span>{currentProtocol.protocol_title}</span>}
                  {currentProtocol.sponsor_name && <span> · {currentProtocol.sponsor_name}</span>}
                </p>
              </div>
              <button
                onClick={() => setCurrentProtocol(null)}
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative" ref={protocolDropdownRef}>
              <div
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer flex items-center justify-between hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                onClick={() => setShowProtocolDropdown(!showProtocolDropdown)}
              >
                <span className="text-gray-500 dark:text-gray-400">Select a protocol to compare against...</span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${showProtocolDropdown ? 'rotate-180' : ''}`}
                />
              </div>

              {/* Protocol Dropdown */}
              {showProtocolDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-80 overflow-hidden">
                  {/* Search input inside dropdown */}
                  <div className="p-2 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        placeholder="Search by protocol number, title, or sponsor..."
                        value={protocolSearch}
                        onChange={e => setProtocolSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Protocol list */}
                  <div className="max-h-60 overflow-y-auto">
                    {filteredProtocols.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        No active protocols found
                      </div>
                    ) : (
                      filteredProtocols.map(protocol => (
                        <button
                          key={protocol.id}
                          onClick={() => handleSelectProtocol(protocol)}
                          className="w-full px-4 py-3 text-left hover:bg-primary-50 dark:hover:bg-primary-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors min-h-[44px] sm:min-h-0"
                        >
                          <p className="font-medium text-gray-900 dark:text-gray-100">{protocol.protocol_number}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {protocol.protocol_title && <span>{protocol.protocol_title}</span>}
                            {protocol.sponsor_name && protocol.protocol_title && <span> · </span>}
                            {protocol.sponsor_name && <span>{protocol.sponsor_name}</span>}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {filteredProtocols.length === 0 && !currentProtocol && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-3">
              No active protocols available. Please create a protocol first.
            </p>
          )}
        </div>
      )}

      {/* Document Extraction Warnings */}
      {documentsReady && currentProtocol && documentStatusAnalysis.hasFailedDocuments && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 dark:text-red-300 font-medium">Document processing failed</p>
              <p className="text-red-700 dark:text-red-300 text-sm mt-1">
                One or more documents failed to process. Please reprocess failed documents before
                proceeding or proceed with partial data.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => {
                    // Find and reprocess all failed documents
                    const failedDocs = uploadedDocuments.filter(
                      d => d.extraction_status === 'failed'
                    );
                    failedDocs.forEach(doc => handleReprocessDocument(doc.id));
                  }}
                  disabled={reprocessingDocId !== null}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
                >
                  <RefreshCw size={14} />
                  Reprocess All Failed
                </button>
                {!acknowledgedWarnings && (
                  <button
                    onClick={() => setAcknowledgedWarnings(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 min-h-[44px] sm:min-h-0"
                  >
                    Proceed Anyway
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {documentsReady &&
        currentProtocol &&
        documentStatusAnalysis.hasCompletedWithErrors &&
        !documentStatusAnalysis.hasFailedDocuments && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-yellow-800 dark:text-yellow-300 font-medium">Some pages had extraction issues</p>
                <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                  The review may be incomplete. Consider reprocessing the document for better
                  results.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => {
                      // Find and reprocess all documents with errors
                      const errorDocs = uploadedDocuments.filter(
                        d => d.extraction_status === 'completed_with_errors'
                      );
                      errorDocs.forEach(doc => handleReprocessDocument(doc.id));
                    }}
                    disabled={reprocessingDocId !== null}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-yellow-700 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
                  >
                    <RefreshCw size={14} />
                    Reprocess Documents
                  </button>
                  <button
                    onClick={handleProceedToReview}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-yellow-700 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-300 min-h-[44px] sm:min-h-0"
                  >
                    Proceed Anyway
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Proceed to Review - only show when no warnings or warnings acknowledged */}
      {canProceed && !documentStatusAnalysis.showWarning && (
        <div className="flex justify-end">
          <button
            onClick={handleProceedToReview}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] sm:min-h-0"
          >
            Proceed to Review
            <ArrowRight size={20} />
          </button>
        </div>
      )}

      {/* Show proceed button after acknowledging failures */}
      {canProceed && documentStatusAnalysis.hasFailedDocuments && acknowledgedWarnings && (
        <div className="flex justify-end">
          <button
            onClick={handleProceedToReview}
            className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors min-h-[44px] sm:min-h-0"
          >
            Proceed with Incomplete Data
            <ArrowRight size={20} />
          </button>
        </div>
      )}

      {/* Cost Estimate Modal */}
      {costEstimate && (
        <CostEstimateModal
          estimate={costEstimate}
          onConfirm={handleConfirmProcessing}
          onCancel={() => {
            setShowCostModal(false);
            setCostEstimate(null);
            setProcessingDocId(null);
          }}
          isOpen={showCostModal}
          isSubmitting={confirmingCost}
        />
      )}

      {/* Success Toast */}
      <Toast message={success} type="success" onClose={clearSuccess} />
    </div>
  );
}
